import { css, type SxProps, styled, type Theme } from '@mui/material/styles';
import noop from 'lodash/noop';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router';

const sharedLinkStyles = (theme: Theme, isDisabled: boolean) => css`
  color: ${theme.palette.primary.main}; 
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  line-height: 28px;
  padding: 0px;
  text-transform: none;

  ${
    isDisabled &&
    css`
    color: ${theme.palette.text.secondary};
    pointer-events: none;
  `
  }
`;
const StyledLink = styled(RouterLink, { shouldForwardProp: prop => prop !== 'isDisabled' })<{ isDisabled: boolean }>`
  ${({ theme, isDisabled }) => sharedLinkStyles(theme, isDisabled)}
`;

const StyledOutsideLink = styled('a')<{ isDisabled: boolean }>`
  ${({ theme, isDisabled }) => sharedLinkStyles(theme, isDisabled)}
`;

interface LinkProps {
  to: string;
  children: ReactNode;
  onClick?: () => void;
  isDisabled?: boolean;
  ['data-test']?: string;
}

interface OutsideLinkProps extends LinkProps {
  openInNewTab?: boolean;
  sx?: SxProps<Theme>;
}

export const Link = (props: LinkProps) => {
  const { to, children, onClick, isDisabled = false } = props;
  const dataTest = props['data-test'] || 'page-link';

  return (
    <StyledLink isDisabled={isDisabled} data-test={dataTest} to={to} onClick={onClick ? onClick : noop}>
      {children}
    </StyledLink>
  );
};

export const OutsideLink = (props: OutsideLinkProps) => {
  const { to, children, onClick, isDisabled = false, openInNewTab = false, sx } = props;
  const dataTest = props['data-test'] || 'page-link';

  return (
    <StyledOutsideLink
      isDisabled={isDisabled}
      onClick={onClick ? onClick : noop}
      href={to}
      target={openInNewTab ? '_blank' : '_self'}
      data-test={dataTest}
      rel="noreferrer"
      sx={sx}
    >
      {children}
    </StyledOutsideLink>
  );
};
